/**
 * FULL-SCREEN ZOOM — pinch, pan and double-tap as arithmetic (D-032).
 *
 * The design authority, 2026-09-01: *"give users the ability to expand the
 * picture to full screen in addition to Retaking or Deleting them."* The
 * expanded picture covers the viewport, and a surveyor reading a nameplate or
 * a breaker label wants closer than that. The Fullscreen API cannot do it —
 * iOS offers it to `<video>` only, and the installed PWA already owns the
 * screen — so the closeness is done here, by hand, with pointer events.
 *
 * This is the whole of the maths, pure, so `PhotoFullscreen.tsx` applies a
 * transform it was handed and never reasons about geometry itself.
 *
 * The model: the picture is centred in the viewport with `transform-origin`
 * at the centre, and rendered as `translate(x, y) scale(scale)`. A picture
 * point `p` (in unscaled px from the centre) therefore lands on screen at
 * `x + p·scale`. Every function below is a consequence of that one line.
 *
 *   - `pinchTo` zooms ABOUT the fingers: whatever was under the pinch stays
 *     under the pinch, which is what makes a zoom feel like grabbing;
 *   - `clampPan` keeps the picture over the screen: the most it may move is
 *     the overflow, `(scale − 1) × half the viewport`, so a pan never drags
 *     the picture off into the dark;
 *   - `settle` snaps a let-go that is within 2% of 1× back to exactly 1×,
 *     centred — a picture left at 1.01× and 3px off would look like a bug;
 *   - `toggleTapZoom` is the double tap: 2.5× about the tap, or back to 1×.
 */

export interface ZoomState {
  scale: number;
  x: number;
  y: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

/**
 * The stage, and — when it is known — the picture's rendered size at 1×. A
 * portrait picture in a portrait phone fills the height and not the width, so
 * at 1.2× there is nothing hidden to the left or right and a pan must not
 * reveal the dark; with `image` the overflow is the picture's, without it the
 * stage's is assumed.
 */
export interface Viewport extends Size {
  image?: Size;
}

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
/** Where a double tap goes. */
export const ZOOM_TAP = 2.5;
/** Below this, letting go is "back to 1×". */
const SETTLE_BELOW = 1.02;

export const IDENTITY: Readonly<ZoomState> = Object.freeze({ scale: 1, x: 0, y: 0 });

/** A straight drag down this far, with nothing left to pan, means "let go" (round 8 task 5). */
export const DISMISS_DRAG_PX = 80;

/**
 * Whether a drag from `origin` to `point` at `scale` closes full screen.
 *
 * Only at 1× — past it the picture has somewhere to pan to. The drop has to
 * clear `DISMISS_DRAG_PX` AND outrun the sideways travel: the final review of
 * round 8 found a horizontal swipe that sagged 81px closed the overlay, and a
 * thumb walking sideways is not letting go of anything.
 */
export function dismissDrag(origin: Point, point: Point, scale: number): boolean {
  if (scale !== 1) return false;
  const dy = point.y - origin.y;
  const dx = point.x - origin.x;
  return dy > DISMISS_DRAG_PX && dy > Math.abs(dx);
}

/**
 * The picture's rendered size at 1× when it is `object-fit: contain`ed in a
 * box: scaled to touch the box on one axis, letterboxed on the other. No
 * natural size (not loaded, or not decodable) means the box itself — the
 * only honest answer, and the one that lets a pan work on a picture the
 * phone cannot show.
 */
export function fitContain(natural: Size, box: Size): Size {
  if (natural.width <= 0 || natural.height <= 0 || box.width <= 0 || box.height <= 0) return { ...box };
  const scale = Math.min(box.width / natural.width, box.height / natural.height);
  return { width: natural.width * scale, height: natural.height * scale };
}

function clampScale(scale: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
}

/** `-0` is what clamping to `-maxX` yields at 1×; a transform string wants `0`. */
function plain(n: number): number {
  return n === 0 ? 0 : n;
}

/** Hold the pan inside the overflow the scale allows. */
export function clampPan(state: ZoomState, view: Viewport): ZoomState {
  const picture = view.image ?? view;
  const maxX = Math.max(0, (picture.width * state.scale - view.width) / 2);
  const maxY = Math.max(0, (picture.height * state.scale - view.height) / 2);
  return {
    scale: state.scale,
    x: plain(Math.min(maxX, Math.max(-maxX, state.x))),
    y: plain(Math.min(maxY, Math.max(-maxY, state.y))),
  };
}

/** Zoom to `scale` about the screen point `about`, from `from`. */
function zoomAbout(from: ZoomState, scale: number, about: Point, view: Viewport): ZoomState {
  const next = clampScale(scale);
  // The picture point under the finger, then placed so it is still under it.
  const px = (about.x - from.x) / from.scale;
  const py = (about.y - from.y) / from.scale;
  return clampPan({ scale: next, x: about.x - px * next, y: about.y - py * next }, view);
}

/**
 * A pinch in progress. `start` is the state when the second finger landed,
 * `startDist`/`startMid` the fingers then, `dist`/`mid` the fingers now. All
 * points are relative to the viewport centre.
 */
export function pinchTo(
  start: ZoomState,
  startDist: number,
  startMid: Point,
  dist: number,
  mid: Point,
  view: Viewport,
): ZoomState {
  const ratio = startDist > 0 ? dist / startDist : 1;
  const zoomed = zoomAbout(start, start.scale * ratio, startMid, view);
  // The fingers may have drifted as well as spread: carry the picture along.
  return clampPan({ scale: zoomed.scale, x: zoomed.x + (mid.x - startMid.x), y: zoomed.y + (mid.y - startMid.y) }, view);
}

/** A one-finger drag. */
export function panBy(state: ZoomState, dx: number, dy: number, view: Viewport): ZoomState {
  return clampPan({ scale: state.scale, x: state.x + dx, y: state.y + dy }, view);
}

/** Letting go. */
export function settle(state: ZoomState): ZoomState {
  return state.scale < SETTLE_BELOW ? { ...IDENTITY } : state;
}

/** A double tap: in about the tap, or all the way out. */
export function toggleTapZoom(state: ZoomState, at: Point, view: Viewport): ZoomState {
  if (state.scale > 1) return { ...IDENTITY };
  return zoomAbout(state, ZOOM_TAP, at, view);
}
